import { useEffect, useState } from "react";
import { PipelineCell, ColHeader } from "./PipelineCell.js";
import { Skeleton } from "./Skeleton.js";
import { AsyncButton } from "./ui/async-button.js";
import type { AttendanceRecord, MyCorrection } from "../pages/AttendancePage.js";
import { fmt, duration, approverRoleLabel, effectiveCheckOut, DEFAULT_SHIFT_HOURS } from "../pages/AttendancePage.js";
import { Badge } from "./ui/badge.js";
import type { HistoryPeriod } from "../lib/historyRange.js";
import { SpecularButton } from "./ui/specular-button.js";
import { DropdownSelect } from "./DropdownButton.js";
import { SegmentedTabs } from "./SegmentedTabs.js";
import { AttendanceRegister } from "./AttendanceRegister.js";
import { Input } from "./ui/input.js";
import { TimeInput } from "./ui/time-input.js";

const IST = "Asia/Kolkata";

const PERIOD_OPTIONS = [
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "custom", label: "Custom" },
];

interface Props {
  loading: boolean;
  history: AttendanceRecord[];
  hasAnyRecords: boolean;
  myCorrections: MyCorrection[];
  period: HistoryPeriod;
  onPeriodChange: (period: HistoryPeriod) => void;
  rangeLabel: string;
  onPrev: () => void;
  onNext: () => void;
  customFrom: string;
  customTo: string;
  onCustomFromChange: (v: string) => void;
  onCustomToChange: (v: string) => void;
  rangeFrom: string;
  rangeTo: string;
  /** "list" is the day-by-day rows; "register" is the month x day-of-month matrix. */
  view: "list" | "register";
  /** Omitted fixes the timeline to `view` and hides the List / Register switcher. */
  onViewChange?: ((view: "list" | "register") => void) | undefined;
  /** Whose register to load. Omitted means the viewer's own, as My History wants. */
  registerEmployeeId?: string | undefined;
  /**
   * The inline "request a correction" editor. Omitted entirely when the timeline shows
   * someone else's record on an approver's screen -- an approver reviews attendance,
   * they do not file the employee's correction request for them. Without it the rows
   * render read-only: no Correct button, no Edit, no editor.
   */
  correctionEditor?: CorrectionEditor | undefined;
  /**
   * Device punches keyed by "YYYY-MM-DD". Supplied only where the viewer is reviewing
   * someone's record (the member panel) -- passing it adds two columns showing what the
   * biometric reader saw next to what the app recorded, so an approver can compare the
   * two without leaving the sheet. Omitted on My History, which stays narrower.
   */
  biometricByDate?: Map<string, { checkIn: string | null; checkOut: string | null }>;
}

export interface CorrectionEditor {
  /** The record id whose editor is open, or null. */
  requestFor: string | null;
  onRequest: (recordId: string) => void;
  onEdit: (record: AttendanceRecord, correction: MyCorrection) => void;
  checkIn: string;
  onCheckInChange: (v: string) => void;
  checkOut: string;
  onCheckOutChange: (v: string) => void;
  reason: string;
  onReasonChange: (v: string) => void;
  busy: boolean;
  err: string;
  onSubmit: (record: AttendanceRecord) => Promise<void>;
  onCancel: () => void;
}

function weekdayShort(dateStr: string): string {
  return new Date(dateStr + "T00:00:00Z").toLocaleDateString("en-IN", { weekday: "short", timeZone: "UTC" });
}
function dayNumber(dateStr: string): string {
  return String(new Date(dateStr + "T00:00:00Z").getUTCDate());
}

const COLS = { day: 76, checkIn: 112, checkOut: 112, bioIn: 104, bioOut: 104, hr: 52, approvedBy: 130, remark: 180 };
const HR_RING_RADIUS = 12;
const HR_RING_CIRC = 2 * Math.PI * HR_RING_RADIUS;

export function AttendanceHistoryTimeline({
  loading, history, hasAnyRecords, myCorrections,
  period, onPeriodChange, rangeLabel, onPrev, onNext,
  customFrom, customTo, onCustomFromChange, onCustomToChange,
  rangeFrom, rangeTo,
  view, onViewChange, registerEmployeeId,
  correctionEditor,
  biometricByDate,
}: Props) {
  const byDate = new Map(history.map((r) => [new Date(r.date).toLocaleDateString("en-CA", { timeZone: IST }), r]));
  const dates = period === "custom" && (!customFrom || !customTo) ? [] : [...byDate.keys()].sort().reverse();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!history.some((r) => r.checkIn && !r.checkOut)) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [history]);

  const [openRemark, setOpenRemark] = useState<{ text: string; approver: string | null } | null>(null);
  // One "which dropdown is open" state for the bar: opening one closes any other.
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  // The day picked in the Register view. Held here, not inside AttendanceRegister,
  // because the detail shown underneath is this file's own list row -- the register
  // must not grow a second day layout of its own.
  const [registerDay, setRegisterDay] = useState<string | null>(null);
  // A new period means the chosen day is no longer on screen; keeping it would show
  // a row for a date the matrix above no longer contains.
  useEffect(() => { setRegisterDay(null); }, [rangeFrom, rangeTo]);

  /**
   * One day's row. A function rather than inline JSX because the Register view
   * renders the very same row for the day the user clicks -- one definition, so
   * the two views can never drift apart.
   */
  function renderDayRow(dateStr: string) {
            const r = byDate.get(dateStr);
            const isPlaceholder = !r;
            const isLeaveRow = !!r?.isLeave;
            const awaitingApproval = r?.exceptionState === "pending";
            const barColor = isLeaveRow || awaitingApproval ? "var(--color-warning-accent)" : r?.isAbsent || r?.isLate ? "var(--color-error)" : r ? "var(--color-success)" : "var(--color-surface-300)";
            const correction = r ? myCorrections.find((c) => c.date.slice(0, 10) === r.date.slice(0, 10)) : undefined;
            const expanded = !!r && !!correctionEditor && correctionEditor.requestFor === r.id;
            const actualHoursNum = r?.checkIn && r?.checkOut ? (new Date(r.checkOut).getTime() - new Date(r.checkIn).getTime()) / 3_600_000 : null;
            const correctionBlocked = actualHoursNum != null && r?.shiftHours != null && actualHoursNum >= r.shiftHours;
            // Display only. It deliberately does NOT feed correctionBlocked above: the
            // person who forgot to punch out is exactly who needs the Correct button, and
            // an assumed full shift would hide it from them.
            const effOut = r ? effectiveCheckOut(r) : { iso: null, assumed: false, shiftHours: DEFAULT_SHIFT_HOURS };
            // Hours are only known when the pair of punches is real. An assumed
            // check-out is a placeholder for a missing one, not a measurement, so it
            // must not produce an hours figure -- that would read as time actually
            // worked. Today's still-open record keeps counting up from check-in.
            const liveHoursNum = !r?.checkIn || effOut.assumed
              ? null
              : ((effOut.iso ? new Date(effOut.iso).getTime() : now) - new Date(r.checkIn).getTime()) / 3_600_000;
            const targetHours = r?.shiftHours ?? DEFAULT_SHIFT_HOURS;
            const hrExceeded = liveHoursNum != null && liveHoursNum > targetHours;
            const hrRingColor = !r ? "var(--color-surface-300)" : hrExceeded ? "var(--color-success)" : r.isLate ? "var(--color-error)" : "var(--color-success)";
            const hrFilledCirc = liveHoursNum != null ? Math.min(liveHoursNum / targetHours, 1) * HR_RING_CIRC : 0;

            return (
              <div key={dateStr} style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", overflow: "hidden", display: "flex" }}>
                <div style={{ width: 4, background: barColor, flexShrink: 0 }} />
                <div style={{ flex: 1, padding: "var(--space-2)" }}>
                  {expanded && r && correctionEditor ? (
                    <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-end", flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                        <span style={{ fontSize: 17, fontWeight: 700 }}>{dayNumber(dateStr)}</span>
                        <span style={{ fontSize: 11, color: "var(--color-surface-700)", textTransform: "uppercase" }}>{weekdayShort(dateStr)}</span>
                      </div>
                      <label style={{ fontSize: 11, color: "var(--color-surface-700)" }}>
                        Corrected Check In
                        <TimeInput value={correctionEditor.checkIn} onChange={correctionEditor.onCheckInChange} aria-label="Corrected check in" className="mt-1 w-32" />
                      </label>
                      <label style={{ fontSize: 11, color: "var(--color-surface-700)" }}>
                        Corrected Check Out
                        <TimeInput value={correctionEditor.checkOut} onChange={correctionEditor.onCheckOutChange} aria-label="Corrected check out" className="mt-1 w-32" />
                      </label>
                      <label style={{ fontSize: 11, color: "var(--color-surface-700)", flex: 1, minWidth: 220 }}>
                        Remark
                        <input type="text" value={correctionEditor.reason} onChange={(e) => correctionEditor.onReasonChange(e.target.value)} placeholder="Add a remark explaining this correction…" style={{ display: "block", width: "100%", boxSizing: "border-box", marginTop: 4, padding: "6px 8px", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 13 }} />
                      </label>
                      <AsyncButton size="sm" onClick={() => correctionEditor.onSubmit(r)} disabled={correctionEditor.busy}>
                        Save
                      </AsyncButton>
                      <button onClick={correctionEditor.onCancel} style={{ padding: "7px 14px", background: "transparent", color: "var(--color-surface-700)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 12, cursor: "pointer" }}>
                        Cancel
                      </button>
                      {correctionEditor.err && <div style={{ width: "100%", color: "var(--color-error)", fontSize: 12 }}>{correctionEditor.err}</div>}
                    </div>
                  ) : isLeaveRow ? (
                    <div style={{ display: "flex", alignItems: "stretch", flexWrap: "nowrap" }}>
                      <PipelineCell width={COLS.day} divider={false}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                          <span style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1 }}>{dayNumber(dateStr)}</span>
                          <span style={{ fontSize: 11, color: "var(--color-surface-700)", textTransform: "uppercase" }}>{weekdayShort(dateStr)}</span>
                        </div>
                      </PipelineCell>

                      <div style={{ flex: 1, display: "flex", alignItems: "center", padding: "0 var(--space-4)", borderLeft: "1px solid var(--color-surface-100)" }}>
                        <span style={{ background: "var(--color-warning-bg-subtle)", color: "var(--color-warning-accent)", border: "1px solid var(--color-warning-border)", padding: "4px 12px", borderRadius: "var(--radius-full)", fontSize: 12, fontWeight: 600 }}>
                          Work Off
                        </span>
                      </div>

                      <div style={{ width: 120, flexShrink: 0, borderLeft: "1px solid var(--color-surface-100)" }} />
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "stretch", flexWrap: "nowrap" }}>
                      <PipelineCell width={COLS.day} divider={false}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
                          <span style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.1 }}>{dayNumber(dateStr)}</span>
                          <span style={{ fontSize: 11, color: "var(--color-surface-700)", textTransform: "uppercase" }}>{weekdayShort(dateStr)}</span>
                        </div>
                      </PipelineCell>

                      <PipelineCell width={COLS.checkIn}>
                        {r?.isAbsent ? (
                          <Badge tone="error" className="px-1.5 py-0 text-[9px]">Absent</Badge>
                        ) : (
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{r ? fmt(r.checkIn) : "—"}</div>
                        )}
                        {awaitingApproval && (
                          <Badge tone="warning" className="px-1.5 py-0 text-[9px]" title="Worked on a weekend, holiday or leave day. Counts as present once approved.">Awaiting approval</Badge>
                        )}
                        {r?.exceptionState === "declined" && (
                          <Badge tone="error" className="px-1.5 py-0 text-[9px]" title="The approver did not approve these hours">Not approved</Badge>
                        )}
                      </PipelineCell>

                      <PipelineCell width={COLS.checkOut}>
                        {effOut.assumed ? (
                          <Badge
                            tone="warning"
                            className="px-1.5 py-0 text-[9px]"
                            title="No check-out recorded for this day"
                          >
                            Not checked out
                          </Badge>
                        ) : (
                          <div style={{ fontSize: 14, fontWeight: 500 }}>{r ? fmt(r.checkOut) : "—"}</div>
                        )}
                      </PipelineCell>

                      {/* What the device saw, beside what the app recorded. A blank is a
                          day with no punch, not a zero -- the same as the columns above. */}
                      {biometricByDate && (
                        <PipelineCell width={COLS.bioIn}>
                          <div title="Biometric check-in" style={{ fontSize: 14, fontWeight: 500, color: "var(--color-surface-700)" }}>
                            {fmt(biometricByDate.get(dateStr)?.checkIn ?? null)}
                          </div>
                        </PipelineCell>
                      )}
                      {biometricByDate && (
                        <PipelineCell width={COLS.bioOut}>
                          <div title="Biometric check-out" style={{ fontSize: 14, fontWeight: 500, color: "var(--color-surface-700)" }}>
                            {fmt(biometricByDate.get(dateStr)?.checkOut ?? null)}
                          </div>
                        </PipelineCell>
                      )}

                      <PipelineCell width={COLS.hr} align="center">
                        <div style={{ position: "relative", width: 28, height: 28 }}>
                          <svg width={28} height={28} style={{ position: "absolute", inset: 0 }}>
                            <circle cx={14} cy={14} r={HR_RING_RADIUS} fill="none" stroke="var(--color-surface-200)" strokeWidth={2.5} />
                            {liveHoursNum != null && (
                              <circle
                                cx={14} cy={14} r={HR_RING_RADIUS} fill="none" stroke={hrRingColor} strokeWidth={2.5} strokeLinecap="round"
                                strokeDasharray={`${hrFilledCirc} ${HR_RING_CIRC - hrFilledCirc}`}
                                transform="rotate(-90 14 14)"
                              />
                            )}
                          </svg>
                          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 700, textAlign: "center", letterSpacing: "-0.2px" }}>
                            {liveHoursNum != null ? duration(r!.checkIn, effOut.iso) : "—"}
                          </div>
                        </div>
                      </PipelineCell>

                      <PipelineCell width={COLS.approvedBy}>
                        {correction?.approverRole ? (
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{approverRoleLabel(correction.approverRole)}</span>
                        ) : (
                          <span style={{ fontSize: 14, color: "var(--color-surface-700)" }}>—</span>
                        )}
                      </PipelineCell>

                      <PipelineCell width={COLS.remark} grow>
                        {correction?.approverRemark ? (
                          <span
                            onClick={() => setOpenRemark({ text: correction.approverRemark!, approver: approverRoleLabel(correction.approverRole) })}
                            title={correction.approverRemark}
                            style={{ fontSize: 12, color: "var(--color-surface-700)", fontStyle: "italic", cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}
                          >
                            "{correction.approverRemark}"
                          </span>
                        ) : (
                          <span style={{ fontSize: 14, color: "var(--color-surface-700)" }}>—</span>
                        )}
                      </PipelineCell>

                      <div style={{ width: 120, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, padding: "0 0 0 var(--space-3)", borderLeft: "1px solid var(--color-surface-100)" }}>
                        {correction?.status === "PENDING" && (
                          <>
                            <span style={{ background: "var(--color-warning-bg)", color: "var(--color-warning-fg)", padding: "3px 10px", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600 }}>Pending</span>
                            {correctionEditor && (
                            <button
                              onClick={() => correctionEditor.onEdit(r!, correction)}
                              title="Edit correction request"
                              style={{ padding: "2px 9px", background: "transparent", color: "var(--color-brand-600)", border: "1px solid var(--color-brand-600)", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600, lineHeight: 1.5, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
                            >
                              Edit
                            </button>
                            )}
                          </>
                        )}
                        {correction?.status === "APPROVED" && (
                          <span style={{ background: "var(--color-success-bg)", color: "var(--color-success-fg)", padding: "3px 10px", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600 }}>Approved</span>
                        )}
                        {correction?.status === "REJECTED" && (
                          <span style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "3px 10px", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600 }}>Declined</span>
                        )}
                        {correctionEditor && !isPlaceholder && !isLeaveRow && !correction && !correctionBlocked && (
                          <button
                            onClick={() => correctionEditor.onRequest(r!.id)}
                            title="Request correction"
                            style={{ padding: "2px 9px", background: "transparent", color: "var(--color-brand-600)", border: "1px solid var(--color-brand-600)", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600, lineHeight: 1.5, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
                          >
                            Correct
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "var(--space-3)", flexWrap: "wrap", marginBottom: "var(--space-4)", flexShrink: 0 }}>
        {onViewChange && (
          <div style={{ display: "flex", alignItems: "center", marginRight: "auto" }}>
            <SegmentedTabs
              value={view}
              onChange={(v) => onViewChange(v as "list" | "register")}
              items={[{ value: "list", label: "List" }, { value: "register", label: "Register" }]}
            />
          </div>
        )}
        <DropdownSelect
          value={period}
          onChange={(v) => onPeriodChange(v as HistoryPeriod)}
          placeholder="Period"
          options={PERIOD_OPTIONS}
          clearable={false}
          ariaLabel="Period"
          open={openFilter === "period"}
          onToggle={() => setOpenFilter((f) => (f === "period" ? null : "period"))}
          onClose={() => setOpenFilter(null)}
        />
        {period === "custom" ? (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <Input type="date" value={customFrom} onChange={(e) => onCustomFromChange(e.target.value)} className="w-auto" aria-label="From date" />
            <span style={{ color: "var(--color-surface-700)" }}>&rarr;</span>
            <Input type="date" value={customTo} onChange={(e) => onCustomToChange(e.target.value)} className="w-auto" aria-label="To date" />
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
            <SpecularButton onClick={onPrev} aria-label="Previous period" className="w-8 px-0">&larr;</SpecularButton>
            <div style={{ fontWeight: 600, fontSize: 14, minWidth: 140, textAlign: "center" }}>{rangeLabel}</div>
            <SpecularButton onClick={onNext} aria-label="Next period" className="w-8 px-0">&rarr;</SpecularButton>
          </div>
        )}
      </div>

      {view === "register" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <AttendanceRegister
            from={rangeFrom}
            to={rangeTo}
            corrections={myCorrections}
            selectedDay={registerDay}
            onSelectDay={setRegisterDay}
            employeeId={registerEmployeeId}
          />
          {registerDay && renderDayRow(registerDay)}
        </div>
      ) : loading ? (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-4)", display: "flex", flexDirection: "column", gap: "var(--space-2)", flex: 1, overflow: "hidden" }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "var(--space-4)", padding: "var(--space-2)", border: "1px solid var(--color-surface-100)", borderRadius: "var(--radius-lg)" }}>
              <Skeleton width={COLS.day - 16} height={20} />
              <Skeleton width={COLS.checkIn - 16} height={16} />
              <Skeleton width={COLS.checkOut - 16} height={16} />
              <Skeleton width={28} height={28} borderRadius="50%" />
              <Skeleton width={COLS.approvedBy - 16} height={16} />
            </div>
          ))}
        </div>
      ) : dates.length === 0 ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: "var(--color-surface-700)" }}>
          {period === "custom" ? "Pick a from and to date." : "No records yet."}
        </div>
      ) : !hasAnyRecords ? (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-12)", flex: 1, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: "var(--color-surface-700)" }}>
          No attendance records for this employee.
        </div>
      ) : (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-4)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <div style={{ display: "flex", flexWrap: "nowrap", marginBottom: "var(--space-2)", flexShrink: 0 }}>
            <div style={{ width: 4, flexShrink: 0 }} />
            <div style={{ flex: 1, padding: "var(--space-1) var(--space-2)" }}>
              <div style={{ display: "flex", flexWrap: "nowrap" }}>
                <PipelineCell width={COLS.day} divider={false}><ColHeader>Day</ColHeader></PipelineCell>
                <PipelineCell width={COLS.checkIn}><ColHeader>Check In</ColHeader></PipelineCell>
                <PipelineCell width={COLS.checkOut}><ColHeader>Check Out</ColHeader></PipelineCell>
                {biometricByDate && <PipelineCell width={COLS.bioIn}><ColHeader>Bio In</ColHeader></PipelineCell>}
                {biometricByDate && <PipelineCell width={COLS.bioOut}><ColHeader>Bio Out</ColHeader></PipelineCell>}
                <PipelineCell width={COLS.hr} align="center"><ColHeader>Hr</ColHeader></PipelineCell>
                <PipelineCell width={COLS.approvedBy}><ColHeader>Approved By</ColHeader></PipelineCell>
                <PipelineCell width={COLS.remark} grow><ColHeader>Remark</ColHeader></PipelineCell>
                <div style={{ width: 120, flexShrink: 0 }} />
              </div>
            </div>
          </div>

          <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {dates.map((dateStr) => renderDayRow(dateStr))}
          </div>
          </div>
        </div>
      )}

      {openRemark && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setOpenRemark(null)}
        >
          <div
            style={{
              background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)",
              width: "min(480px, 90vw)", maxHeight: "80vh", overflow: "auto",
              padding: 24, display: "flex", flexDirection: "column", gap: 12,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Approval Remark</h3>
              <button onClick={() => setOpenRemark(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--color-surface-400)", lineHeight: 1 }}>×</button>
            </div>
            {openRemark.approver && <div style={{ fontSize: 12, color: "var(--color-surface-700)" }}>Approved by {openRemark.approver}</div>}
            <div style={{ fontSize: 14, color: "var(--color-surface-900)", whiteSpace: "pre-wrap" }}>{openRemark.text}</div>
          </div>
        </div>
      )}
    </div>
  );
}
